/**
 * Component for BackstageAlbum
 *
 * @module component/BackstageAlbum
 */

import { List } from 'immutable';
import noop from 'lodash/noop';
import React, { type ReactElement, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';
import { Link } from 'react-router';

import { getCatalogDetailsForBackstage } from '../actions';
import { BackstageLoggerDefaultArgs, messages } from '../constants';
import * as D from '../deps';
import { getBackstageAnnotation, getCommonBackstageProps, isValidPandoraId } from '../utils';

import BackstageTrackList from './BackstageTrackList';
import BackstageAlbumGraphQL from './pages/BackstageAlbum/BackstageAlbum';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { ADDTOCOLLECTION, REMOVEFROMCOLLECTION, SHARE } = D.Constants.SourceCard.SourceCardConstants;
const { STATION_TRIGGER } = D.Constants.SourceCard.SourceCardTriggerTypes;
const BackstageTrackListWithLogger = D.withVisibilityLogger(
  {
    contentSection: D.Constants.ContentSections.BackstageContentSections.TRACK_LIST,
  },
  { staticHeight: D.RowItemHeights.SMALL, useMemo: true },
)(BackstageTrackList);

export interface IBackstageAlbumStateProps {
  pandoraId?: string | null;
  albumTracks?: List<$TSFixMe>;
  albumTrackIds?: List<string>;
  sourceCardMenuItems?: $TSFixMe[];
  hasInteractiveRights?: boolean;
  isRightRailActive?: boolean;
  isPaused?: boolean;
  isPlayingCurrently?: boolean;
  inCollection?: boolean;
  playbackMessages?: $TSFixMe;
  albumImgUrl?: string;
  title?: string;
  albumCopyrights?: string;
  shareableUrlPath?: string;
  totalTracks?: number;
  isCompilation?: boolean;
  releaseYear?: number;
  artistPandoraId?: string;
  artistName?: string;
  artistUrl?: string;
  artistHasNoRadio?: boolean;
  descriptionTitle?: $TSFixMe;
  description?: string;
  shouldRenderBSPSidebarButtons?: ReturnType<typeof D.shouldRenderBSPSidebarButtons>;
  isInitialAppLoad?: boolean;
  // T3 specific
  showPremiumAccess?: ReturnType<typeof D.Utils.Listener.showPremiumAccess>;
  isMobileWeb?: ReturnType<typeof D.isMobileWeb>;
  disableHover?: boolean;
}

export interface IBackstageAlbumDispatchProps {
  openDropdown: typeof D.openDropdown;
  closeDropdown: typeof D.closeDropdown;
  addCollectionItem: typeof D.addCollectionItem;
  removeCollectionItem: typeof D.removeCollectionItem;
  playResumeSource: typeof D.playResumeSource;
  pauseTrack: typeof D.Actions.UseCases.pauseTrack;
  openShareModal: $TSFixMe;
  closeModal: typeof D.closeModal;
  startArtistStation: typeof D.Actions.UseCases.startStation;
  openModal: typeof D.openModal;
  getCatalogDetailsForBackstage: typeof getCatalogDetailsForBackstage;
}

export interface IBackstageAlbumOwnProps
  extends React.PropsWithChildren<{
    params?: $TSFixMe;
    // T3 specific
    onDemandIsEnabled?: boolean;
    encryptedPandoraId?: string;
  }> {}

export type IBackstageAlbumProps = IBackstageAlbumStateProps &
  IBackstageAlbumDispatchProps &
  IBackstageAlbumOwnProps;

export class BackstageAlbum extends React.Component<IBackstageAlbumProps> {
  componentDidMount(): void {
    if (this.props.pandoraId) {
      this.props.getCatalogDetailsForBackstage(this.props.pandoraId, true);
    }
  }

  shouldComponentUpdate(nextProps: IBackstageAlbumProps): boolean {
    return D.arePropsDifferent(nextProps, this.props);
  }

  componentDidUpdate(prevProps: IBackstageAlbumProps): void {
    // Prevent a redundant call using the unencrypted pandoraId, if we already made data calls using the encryptedPandoraId
    const isPandoraIdUpdated = prevProps.pandoraId !== this.props.encryptedPandoraId;
    if (
      this.props.pandoraId &&
      prevProps.pandoraId !== this.props.pandoraId &&
      isPandoraIdUpdated
    ) {
      this.props.getCatalogDetailsForBackstage(this.props.pandoraId, true);
    }
  }

  static contextType = D.AppContext;

  getStartArtistStationMessage(): ReactElement | null {
    if (this.props.artistHasNoRadio) return null;
    return (
      <FormattedMessage
        {...messages.startArtistStationBasedOnArtist}
        values={{ artistName: this.props.artistName }}
      />
    );
  }

  getStartStationButton(): ReactElement[] | null {
    if (this.props.artistHasNoRadio) return null;
    return [
      <D.ButtonT3 key={0} size={D.ButtonT3Size.SMALL} onClick={this.onModalStartStationClick}>
        <FormattedMessage {...this.props.playbackMessages.startArtistStation} />
      </D.ButtonT3>,
    ];
  }

  getIntegrationDataFactory = (name: $TSFixMe): $TSFixMe => {
    return (): $TSFixMe => {
      const contexts = {
        buttonRow: {
          ...BackstageLoggerDefaultArgs.controlBar,
          ...{ pandoraId: this.props.pandoraId },
        },
      };
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because express... Remove this comment to see the full error message
      return contexts[name] || BackstageLoggerDefaultArgs[name];
    };
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getShareParentSource = () => {
    return {
      shareParentSourceId: this.props.pandoraId,
      shareParentSourceType: D.PandoraTypes.ALBUM,
    };
  };

  onModalStartStationClick = (): void => {
    this.props.closeModal();
    // TODO: WEBCA-2936 investigate why we're only passing these props through
    // to START_STATION.
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ pandoraId: string | null; reroute... Remove this comment to see the full error message
    this.props.startArtistStation({
      pandoraId: this.props.artistPandoraId ?? null,
      rerouteBasedOnTier: true,
      componentName: 'BackstageAlbum',
    });
  };

  triggerStartArtistStationCoachmark = (): void => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{
        pandoraId: this.props.pandoraId,
        componentEventType: STATION_TRIGGER,
      },
    });
    this.props.openModal({
      modalType: 'ModalCoachMark',
      body: (
        <span>
          <FormattedMessage {...messages.albumStationsNotAvailable} />{' '}
          {this.getStartArtistStationMessage()}
        </span>
      ),
      buttons: this.getStartStationButton(),
    });
  };

  playOnClick = (): void => {
    const { pandoraId, showPremiumAccess } = this.props;
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: AUDIO_PLAY_TRACK },
    });
    // TODO: WEBCA-2934 investigate why we're only passing these props through
    // to PLAY_RESUME_SOURCE.
    // @ts-expect-error
    this.props.playResumeSource({
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      forceRestartSourceIfPlaying: showPremiumAccess,
      id: pandoraId,
      pageUrl: this.props.shareableUrlPath,
      pandoraType: null,
      shouldShuffle: false,
    });
  };

  pauseOnClick = (): void => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: PAUSE },
    });
    this.props.pauseTrack();
  };

  onShareClick = (): void => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: SHARE },
    });
    this.props.openShareModal(null, this.getShareParentSource());
  };

  addToMusicClick = (): void => {
    const { pandoraId } = this.props;
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: ADDTOCOLLECTION },
    });
    // TODO: WEBCA-2961 investigate why we're only passing these props through
    // to ADD_COLLECTION_ITEM.
    // @ts-expect-error
    this.props.addCollectionItem({ pandoraId });
  };

  removeMusicClick = (): void => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: REMOVEFROMCOLLECTION },
    });
    this.props.removeCollectionItem({ pandoraId: this.props.pandoraId });
  };

  onHeaderLinkClick = (e: $TSFixMe): void => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.header,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  renderSidebarButtons = (): ReactElement => {
    const { inCollection, isPaused, title } = this.props;
    const playOnClick = !this.props.isPlayingCurrently || isPaused ? this.playOnClick : noop;
    const pauseOnClick = this.props.isPlayingCurrently && !isPaused ? this.pauseOnClick : noop;
    const addToMusicOnClick = !inCollection ? this.addToMusicClick : noop;
    const removeFromMusicOnClick = inCollection ? this.removeMusicClick : noop;
    const disabledButtons = [D.ButtonRowType.CREATE_STATION];

    if (this.props.hasInteractiveRights === false) {
      // @ts-expect-error TS(2345) FIXME: Argument of type '"play"' is not assignable to par... Remove this comment to see the full error message
      disabledButtons.push(D.ButtonRowType.PLAY);
    }

    const buttonRowProps = {
      buttonTextType: D.ButtonTextType.PLAY_ALBUM,
      createStationOnClick: this.triggerStartArtistStationCoachmark,
      disabledButtons,
      ellipsisSourceCardMenuItems: this.props.sourceCardMenuItems,
      entityName: title,
      isSidebar: true,
      pauseOnClick,
      playOnClick,
      removeFromMusicOnClick,
      shareOnClick: this.onShareClick,
    };

    if (this.props.onDemandIsEnabled) {
      // @ts-expect-error TS(2339) FIXME: Property 'addToMusicOnClick' does not exist on typ... Remove this comment to see the full error message
      buttonRowProps.addToMusicOnClick = addToMusicOnClick;
    } else {
      // @ts-expect-error TS(2339) FIXME: Property 'hiddenButtons' does not exist on type '{... Remove this comment to see the full error message
      buttonRowProps.hiddenButtons = [D.ButtonRowType.MORE];
    }

    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('buttonRow')}>
        {/* @ts-expect-error TS(2559) FIXME: Type '{ playOnClick: (...args: any[]) => void; pau... Remove this comment to see the full error message */}
        <D.ButtonRowWithLogger {...buttonRowProps} />
      </D.AppContextIntegrationData>
    );
  };

  renderAlbumDescription(): ReactElement | undefined {
    if (!this.props.description) {
      return;
    }

    return (
      <D.ContentSection isAlbumDescription={true}>
        <D.DescriptionBackstageWithLogger
          // @ts-expect-error TS(2322) FIXME: Type '{ text: any; linkUrl: string; isExpanded: bo... Remove this comment to see the full error message
          text={this.props.description}
          linkUrl={D.getBackstageFullDescriptionPathForShareUrl(this.props.shareableUrlPath ?? '')}
          isExpanded={false}
          linkText={messages.readMore}
          disableHover={this.props.disableHover}
        />
      </D.ContentSection>
    );
  }

  renderTracks(): ReactElement | undefined {
    const trackIds = this.props.albumTrackIds;
    if (!trackIds) {
      return;
    }
    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('trackList')}>
        <BackstageTrackListWithLogger
          // @ts-expect-error TS(2322) FIXME: Type '{ isCompilation: any; tracks: any; isAlbumBS... Remove this comment to see the full error message
          isCompilation={this.props.isCompilation}
          tracks={trackIds}
          isAlbumBSP={true}
          playablePandoraId={this.props.pandoraId}
          elementHeight={D.RowItemHeights.SMALL}
          isAlbumRadioOnly={!this.props.hasInteractiveRights}
          isAlbumCollected={this.props.inCollection}
          shareParentSource={this.getShareParentSource()}
        />
      </D.AppContextIntegrationData>
    );
  }

  renderStructuredMetaData = (): ReactElement | undefined => {
    const { artistPandoraId } = this.props;
    if (!artistPandoraId) {
      return;
    }
    return (
      <D.StructuredMetadata
        isAlbum={true}
        shareableUrlPath={this.props.shareableUrlPath}
        artistShareableUrlPath={this.props.artistUrl}
        title={this.props.title}
        artistName={this.props.artistName}
        pandoraId={this.props.artistPandoraId}
        description={this.props.description}
        image={this.props.albumImgUrl}
        albumTracks={this.props.albumTracks}
      />
    );
  };

  renderMainContent = (): ReactNode => {
    if (this.props.children) {
      // @ts-expect-error TS(2769) FIXME: No overload matches this call.
      return React.cloneElement(this.props.children, this.props);
    }
    return (
      <React.Fragment>
        {this.renderAlbumDescription()}
        {this.renderTracks()}
        {this.props.albumCopyrights && (
          // @ts-expect-error TS(2322) FIXME: Type '{ copyrights: any; }' is not assignable to t... Remove this comment to see the full error message
          <D.CopyrightWithLogger copyrights={this.props.albumCopyrights} />
        )}
      </React.Fragment>
    );
  };

  render(): ReactNode {
    const { title, artistName, totalTracks, releaseYear, artistUrl, shareableUrlPath } = this.props;

    let subTitleLrg;
    let subTitleSm;

    if (artistName) {
      if (artistUrl) {
        subTitleLrg = (
          <span>
            <FormattedMessage {...messages.albumBy} />
            <Link
              className={D.HeaderBackstageStyles.SUBTITLE_LINK}
              to={artistUrl}
              data-qa={D.QA.HSTSTL}
              onClick={this.onHeaderLinkClick}
            >
              {artistName}
            </Link>
          </span>
        );
      } else {
        subTitleLrg = (
          <span>
            <FormattedMessage {...messages.albumBy} />
            {artistName}
          </span>
        );
      }
    }
    if (totalTracks && releaseYear) {
      subTitleSm = (
        <span data-qa={D.QA.HSTST}>
          <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
          {` - ${releaseYear}`}
        </span>
      );
    } else if (totalTracks) {
      subTitleSm = (
        <span data-qa={D.QA.HSTST}>
          <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
        </span>
      );
    } else if (releaseYear) {
      subTitleSm = <span data-qa={D.QA.HSTST}>{releaseYear}</span>;
    }

    const headerProps = {
      badges: this.props.hasInteractiveRights === false ? List([D.BadgeTypes.RADIO]) : null,
      disableHover: this.props.disableHover,
      subTitleLrg,
      subTitleSm,
      title: title || '',
      titleUrl: shareableUrlPath,
    };

    return (
      <D.BackstageLayout
        // @ts-expect-error TS(2322) FIXME: Type '{ children?: ReactNode; headerProps: { title... Remove this comment to see the full error message
        headerProps={headerProps}
        heroImageUrl={this.props.albumImgUrl}
        heroImageLinkUrl={this.props.shareableUrlPath}
        renderSidebarButtons={
          this.props.shouldRenderBSPSidebarButtons ? this.renderSidebarButtons : null
        }
        renderArtworkPlayButton={this.props.isMobileWeb}
        renderMainContent={this.renderMainContent}
        entityIdentifier={this.props.pandoraId}
        entityGetterFunc={this.props.getCatalogDetailsForBackstage}
        playOnClick={this.playOnClick}
        {...this.props}
      />
    );
  }
}

export const mapStateToProps: MapStateToProps<
  IBackstageAlbumStateProps,
  IBackstageAlbumOwnProps,
  $TSFixMe
> = (state, ownProps) => {
  const encryptedPandoraId = ownProps && ownProps.params && ownProps.params.pandoraId;
  const annotation = getBackstageAnnotation(state, encryptedPandoraId);

  if (
    !annotation ||
    annotation.isEmpty() ||
    ![D.PandoraTypes.ALBUM].includes(annotation.get('type'))
  ) {
    return {
      pandoraId: encryptedPandoraId ? encryptedPandoraId : null,
    };
  }
  const pandoraId = annotation.get('pandoraId');
  const catalogDetails = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(
    state,
    pandoraId,
  );
  const props = getCommonBackstageProps(state, encryptedPandoraId, pandoraId);

  if (catalogDetails && !catalogDetails.isEmpty() && annotation) {
    const getAnnotations = D.getInDomain('catalog.utils.getAnnotations');
    // @ts-expect-error TS(2339) FIXME: Property 'albumTrackIds' does not exist on type '{... Remove this comment to see the full error message
    props.albumTrackIds = D.getFromMap(annotation, 'tracks') || List();
    // @ts-expect-error TS(2339) FIXME: Property 'albumTracks' does not exist on type '{ p... Remove this comment to see the full error message
    props.albumTracks = props.albumTrackIds.map(trackPandoraId => {
      return getAnnotations(state, trackPandoraId);
    });
    // @ts-expect-error TS(2339) FIXME: Property 'sourceCardMenuItems' does not exist on t... Remove this comment to see the full error message
    props.sourceCardMenuItems = D.getSourceCardMenuItems({
      state,
      pandoraId,
      pandoraType: D.PandoraTypes.ALBUM,
      isBackstagePage: true,
    });

    const rightsInfo = D.getFromMap(annotation, 'rightsInfo');
    // @ts-expect-error TS(2339) FIXME: Property 'hasInteractiveRights' does not exist on ... Remove this comment to see the full error message
    props.hasInteractiveRights = D.getFromMap(rightsInfo, 'hasInteractive');

    // @ts-expect-error TS(2339) FIXME: Property 'isRightRailActive' does not exist on typ... Remove this comment to see the full error message
    props.isRightRailActive =
      D.getInDomain('ads.utils.getIsRightRailActive', false)(state) ||
      D.getInDomain('ads.utils.getIsElementCenteredWithAds', false)(state);
    // @ts-expect-error TS(2339) FIXME: Property 'isPaused' does not exist on type '{ pand... Remove this comment to see the full error message
    props.isPaused = !D.getInDomain('playback.utils.isAudioPlaying', false)(state);
    // @ts-expect-error TS(2339) FIXME: Property 'isPlayingCurrently' does not exist on ty... Remove this comment to see the full error message
    props.isPlayingCurrently = D.getInDomain('playback.utils.isSourcePlayingCurrently', false)(
      state,
      pandoraId,
    );
    // @ts-expect-error TS(2339) FIXME: Property 'inCollection' does not exist on type '{ ... Remove this comment to see the full error message
    props.inCollection = D.getInDomain('collection.utils.isPandoraIdInCollection', false)(
      state,
      pandoraId,
    );
    // @ts-expect-error TS(2339) FIXME: Property 'playbackMessages' does not exist on type... Remove this comment to see the full error message
    props.playbackMessages = D.getInDomain('playback.constants.PlaybackMessages', null);

    // @ts-expect-error TS(2339) FIXME: Property 'albumImgUrl' does not exist on type '{ p... Remove this comment to see the full error message
    props.albumImgUrl = D.getArt(annotation, 1080, state);
    // @ts-expect-error TS(2339) FIXME: Property 'title' does not exist on type '{ pandora... Remove this comment to see the full error message
    props.title = D.getFromMap(annotation, 'name');
    // @ts-expect-error TS(2339) FIXME: Property 'albumCopyrights' does not exist on type ... Remove this comment to see the full error message
    props.albumCopyrights = D.getFromMap(catalogDetails, 'copyright');
    // @ts-expect-error TS(2339) FIXME: Property 'shareableUrlPath' does not exist on type... Remove this comment to see the full error message
    props.shareableUrlPath = D.getFromMap(annotation, 'shareableUrlPath');
    // @ts-expect-error TS(2339) FIXME: Property 'totalTracks' does not exist on type '{ p... Remove this comment to see the full error message
    props.totalTracks = D.getFromMap(annotation, 'trackCount');
    // @ts-expect-error TS(2339) FIXME: Property 'isCompilation' does not exist on type '{... Remove this comment to see the full error message
    props.isCompilation = D.getFromMap(annotation, 'isCompilation');

    if (D.getFromMap(annotation, 'releaseDate')) {
      // @ts-expect-error TS(2339) FIXME: Property 'releaseYear' does not exist on type '{ p... Remove this comment to see the full error message
      props.releaseYear = new Date(D.getFromMap(annotation, 'releaseDate')).getFullYear();
    }

    const artistPandoraId = D.getFromMap(annotation, 'artistId');
    // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    const artistAnnotation = D.getAnnotationsById(state, artistPandoraId);
    const artistLink = D.getInDomain('catalog.utils.getCatalogEntityLink')(state, artistPandoraId);
    // @ts-expect-error TS(2339) FIXME: Property 'artistPandoraId' does not exist on type ... Remove this comment to see the full error message
    props.artistPandoraId = artistPandoraId;
    // @ts-expect-error TS(2339) FIXME: Property 'artistName' does not exist on type '{ pa... Remove this comment to see the full error message
    props.artistName = artistLink.name;
    // @ts-expect-error TS(2339) FIXME: Property 'artistUrl' does not exist on type '{ pan... Remove this comment to see the full error message
    props.artistUrl = artistLink.shareableUrlPath;
    // @ts-expect-error TS(2339) FIXME: Property 'artistHasNoRadio' does not exist on type... Remove this comment to see the full error message
    props.artistHasNoRadio = D.Utils.Rights.hasNoRadio(artistAnnotation);
    // @ts-expect-error TS(2339) FIXME: Property 'descriptionTitle' does not exist on type... Remove this comment to see the full error message
    props.descriptionTitle = <FormattedMessage {...messages.albumDescription} />;
    // @ts-expect-error TS(2339) FIXME: Property 'description' does not exist on type '{ p... Remove this comment to see the full error message
    props.description = D.getFromMap(catalogDetails, 'description');
  }

  // @ts-expect-error TS(2339) FIXME: Property 'showPremiumAccess' does not exist on typ... Remove this comment to see the full error message
  props.showPremiumAccess = D.Utils.Listener.showPremiumAccess(state);
  // @ts-expect-error TS(2339) FIXME: Property 'shouldRenderBSPSidebarButtons' does not ... Remove this comment to see the full error message
  props.shouldRenderBSPSidebarButtons = D.shouldRenderBSPSidebarButtons(state);
  // @ts-expect-error TS(2339) FIXME: Property 'isMobileWeb' does not exist on type '{ p... Remove this comment to see the full error message
  props.isMobileWeb = D.isMobileWeb(state);
  // @ts-expect-error TS(2339) FIXME: Property 'disableHover' does not exist on type '{ ... Remove this comment to see the full error message
  props.disableHover = D.disableHoverStates(state);
  // @ts-expect-error TS(2339) FIXME: Property 'isInitialAppLoad' does not exist on type... Remove this comment to see the full error message
  props.isInitialAppLoad = D.getInDomain('application.utils.isInitialLoad', false)(state);

  return props;
};

const mapDispatchToProps: MapDispatchToProps<
  IBackstageAlbumDispatchProps,
  IBackstageAlbumOwnProps
> = {
  addCollectionItem: D.addCollectionItem,
  closeDropdown: D.closeDropdown,
  closeModal: D.closeModal,
  getCatalogDetailsForBackstage,
  openDropdown: D.openDropdown,
  openModal: D.openModal,
  openShareModal: D.openShareModal,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  playResumeSource: D.playResumeSource,
  removeCollectionItem: D.removeCollectionItem,
  startArtistStation: D.Actions.UseCases.startStation,
};

export interface IBackstageAlbumBaseOwnProps
  extends React.PropsWithChildren<{
    pandoraId?: string | null;
    isInitialAppLoad?: boolean;
  }> {}

export type IBackstageAlbumBaseProps = IBackstageAlbumBaseOwnProps & IBackstageAlbumProps;

const BackstageAlbumBase: React.FC<IBackstageAlbumBaseProps> = props => {
  const { pandoraId, children, isInitialAppLoad } = props;

  if (isInitialAppLoad || !isValidPandoraId(pandoraId)) {
    return <BackstageAlbum {...props} />;
  }

  return <BackstageAlbumGraphQL pandoraId={pandoraId}>{children}</BackstageAlbumGraphQL>;
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageAlbumBase);
